/**
 * HybridStorageAdapter.ts - Hybrid Storage Adapter for Cachr
 * 
 * CHANGE HISTORY:
 * - 251125_2218 danielbeer: Add TypeScript types
 * - 251019_1122 [0b22d83] danielbeer: feat: add IndexedDB and hybrid storage adapters
 */
import { LocalStorageAdapter } from './CachrManager';
import { IndexedDBAdapter } from './IndexedDBAdapter';
import type { HybridStorageOptions, StorageAdapter } from './types';

const SIZE_THRESHOLD = 5 * 1024 * 1024; // 5MB threshold
const METADATA_KEY_PREFIX = '_hybrid_meta_';

type StorageType = 'localStorage' | 'indexedDB';

export class HybridStorageAdapter implements StorageAdapter {
  private localStorage: LocalStorageAdapter;
  private indexedDB: IndexedDBAdapter;
  private threshold: number;
  private initPromise: Promise<IDBDatabase>;

  constructor(options: HybridStorageOptions = {}) {
    this.localStorage = new LocalStorageAdapter();
    this.indexedDB = new IndexedDBAdapter(options);
    this.threshold = options.sizeThreshold || SIZE_THRESHOLD;
    this.initPromise = this.indexedDB.init();
  }

  /**
   * Determine storage location for an item
   */
  getStorageType(key: string): StorageType {
    try {
      const meta = this.localStorage.getItem(METADATA_KEY_PREFIX + key);
      return meta ? JSON.parse(meta).storageType : 'localStorage';
    } catch (e) {
      return 'localStorage';
    }
  }

  /**
   * Store metadata about where item is stored
   */
  setStorageMetadata(key: string, storageType: StorageType, size: number): void {
    try {
      this.localStorage.setItem(
        METADATA_KEY_PREFIX + key,
        JSON.stringify({ storageType, size, timestamp: Date.now() })
      );
    } catch (e) {
      console.warn('Failed to store metadata:', e);
    }
  }

  /**
   * Remove storage metadata
   */
  removeStorageMetadata(key: string): void {
    try {
      this.localStorage.removeItem(METADATA_KEY_PREFIX + key);
    } catch (e) {
      // Ignore
    }
  }

  /**
   * Calculate approximate size of value
   */
  calculateSize(value: string): number {
    if (typeof value === 'string') {
      return value.length * 2; // UTF-16 encoding
    }
    return JSON.stringify(value).length * 2;
  }

  /**
   * Get item from appropriate storage
   */
  async getItem(key: string): Promise<string | null> {
    const storageType = this.getStorageType(key);

    if (storageType === 'indexedDB') {
      try {
        return await this.indexedDB.getItem(key);
      } catch (e) {
        console.error('IndexedDB getItem failed, trying localStorage:', e);
        return this.localStorage.getItem(key);
      }
    }

    return this.localStorage.getItem(key);
  }

  /**
   * Set item in appropriate storage based on size
   */
  async setItem(key: string, value: string): Promise<void> {
    const size = this.calculateSize(value);
    const useIndexedDB = size >= this.threshold;

    if (useIndexedDB) {
      try {
        await this.indexedDB.setItem(key, value);
        this.setStorageMetadata(key, 'indexedDB', size);
        
        // Remove from localStorage if it was there before
        try {
          this.localStorage.removeItem(key);
        } catch (e: unknown) {
          // Ignore
        }
        
        return;
      } catch (e: unknown) {
        console.error('Failed to store in IndexedDB, falling back to localStorage:', e);
        // Fall through to localStorage
      }
    }

    // Try localStorage
    try {
      this.localStorage.setItem(key, value);
      this.setStorageMetadata(key, 'localStorage', size);
    } catch (e) {
      // If localStorage fails due to quota, try IndexedDB as fallback
      if (e.name === 'QuotaExceededError') {
        console.warn('localStorage quota exceeded, using IndexedDB fallback');
        await this.indexedDB.setItem(key, value);
        this.setStorageMetadata(key, 'indexedDB', size);
      } else {
        throw e;
      }
    }
  }

  /**
   * Remove item from appropriate storage
   */
  async removeItem(key: string): Promise<void> {
    const storageType = this.getStorageType(key);

    if (storageType === 'indexedDB') {
      try {
        await this.indexedDB.removeItem(key);
      } catch (e) {
        console.error('IndexedDB removeItem failed:', e);
      }
    }

    // Always try localStorage too (cleanup)
    try {
      this.localStorage.removeItem(key);
    } catch (e) {
      // Ignore
    }

    this.removeStorageMetadata(key);
  }

  /**
   * Get all keys from both storage locations
   */
  async getAllKeys(): Promise<string[]> {
    const localKeys = [];
    const prefix = METADATA_KEY_PREFIX;

    // Get keys from metadata
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          localKeys.push(key.substring(prefix.length));
        }
      }
    } catch (e) {
      console.error('Failed to get localStorage keys:', e);
    }

    return localKeys;
  }

  /**
   * Clear all items from both storage locations
   */
  async clear(): Promise<void> {
    // Clear IndexedDB
    try {
      await this.indexedDB.clear();
    } catch (e) {
      console.error('Failed to clear IndexedDB:', e);
    }

    // Clear localStorage items and metadata
    const keys = await this.getAllKeys();
    keys.forEach(key => {
      try {
        this.localStorage.removeItem(key);
        this.removeStorageMetadata(key);
      } catch (e) {
        // Ignore
      }
    });
  }

  /**
   * Get storage statistics
   */
  async getStats(): Promise<{
    localStorage: { size: number; count: number };
    indexedDB: { size: number; count: number };
    total: { size: number; count: number };
  }> {
    const keys = await this.getAllKeys();
    let localStorageSize = 0;
    let indexedDBSize = 0;
    let localStorageCount = 0;
    let indexedDBCount = 0;

    for (const key of keys) {
      try {
        const meta = this.localStorage.getItem(METADATA_KEY_PREFIX + key);
        if (meta) {
          const { storageType, size } = JSON.parse(meta);
          if (storageType === 'indexedDB') {
            indexedDBSize += size;
            indexedDBCount++;
          } else {
            localStorageSize += size;
            localStorageCount++;
          }
        }
      } catch (e) {
        // Ignore
      }
    }

    return {
      localStorage: {
        size: localStorageSize,
        count: localStorageCount
      },
      indexedDB: {
        size: indexedDBSize,
        count: indexedDBCount
      },
      total: {
        size: localStorageSize + indexedDBSize,
        count: localStorageCount + indexedDBCount
      }
    };
  }

  /**
   * Close connections
   */
  close(): void {
    this.indexedDB.close();
  }
}
